import { useContext, useEffect, useMemo, useState } from 'react'
import { SelectOption } from '@Components/Selectors/Select'
import { StoresContext } from '@App/contexts'
import { useNavigate } from 'react-router'
import Navigation from '@App/navigation'
import { getUsagePeriod } from '@App/helpers/work-entities/get-usage-period'
import { PayeeRoyalties } from '@Entities/statements/payee-royalties'

export type RoyaltiesStatementsContext = ReturnType<typeof useRoyaltiesStatements>

export const useRoyaltiesStatements = () => {
    const { statementsStore, rightsholderStore, preloaderStore } = useContext(StoresContext)
    const [sidebarIsOpen, setSidebarIsOpen] = useState<boolean>(false)
    const [selectedPayee, setSelectedPayee] = useState<SelectOption>()
    const [selectedDsp, setSelectedDsp] = useState<SelectOption | null>(null)
    const [selectedStatements, setSelectedStatements] = useState<string[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const navigate = useNavigate()

    const fromTopPublisherIpId = useMemo(() => selectedPayee?.value.split('_')[1], [selectedPayee])

    const filteredRows = useMemo(() => {
        if (!selectedPayee || !statementsStore.selectedUsagePeriod || !selectedDsp) {
            return []
        }

        return statementsStore.statements
            .map((s) => ({ ...s, payee: selectedPayee.label }))
            .filter(
                (s) =>
                    (s.dsp === selectedDsp.value || selectedDsp.value === 'All') &&
                    `${s.month.toString().padStart(2, '0')}-${s.year}` === statementsStore.selectedUsagePeriod.value &&
                    (!fromTopPublisherIpId || s.id.split('_')[0] === fromTopPublisherIpId.toString())
            )
            .sort((a, b) => a.dsp.localeCompare(b.dsp) || b.year - a.year || b.month - a.month)
    }, [
        statementsStore.statements,
        selectedPayee,
        statementsStore.selectedUsagePeriod,
        selectedDsp,
        fromTopPublisherIpId,
    ])

    const payeeOptionsForSelect = useMemo(() => {
        const payeeOptionData = statementsStore.royalties ?? []
        return payeeOptionData
            .slice()
            .map((royalty) => ({
                label: royalty.payeeName,
                value: `${royalty.ipId}_${royalty.fromTopPublisherIpId}`,
                id: royalty.ipId.toString(),
            }))
            .sort((a: any, b: any) => (a.label < b.label ? -1 : 1))
    }, [statementsStore.royalties])

    const useRoyaltiesForPeriod = async (usagePeriod: SelectOption) => {
        setLoading(true)
        await statementsStore.loadRoyaltiesForPeriod(usagePeriod)
        if (selectedPayee) {
            await statementsStore.loadStatements(selectedPayee.id)
        }
        setLoading(false)
    }

    const setPayeeAndLoadStatements = async (withThisPayee: SelectOption) => {
        setLoading(true)
        try {
            setSelectedPayee(withThisPayee)
            setSelectedDsp(dspOptions[0])
            setSelectedStatements([])
            await statementsStore.loadStatements(withThisPayee.id)
        } finally {
            setLoading(false)
        }
    }

    const handleNavigateAndSetPeriod = async (payeeRoyalties: PayeeRoyalties) => {
        navigate(
            Navigation.rightholder.royalties.statement(
                rightsholderStore.currentRightsholderId,
                payeeRoyalties.ipId,
                statementsStore.selectedUsagePeriod?.label ?? ''
            )
        )

        await setPayeeAndLoadStatements({
            label: payeeRoyalties.payeeName,
            value: `${payeeRoyalties.ipId.toString()}_${payeeRoyalties.fromTopPublisherIpId.toString()}`,
            id: payeeRoyalties.ipId.toString(),
        })
    }

    const setDsp = (dsp: SelectOption) => {
        setSelectedDsp(dsp)
        setSelectedStatements([])
    }

    const selectAll = () => {
        setSelectedStatements(filteredRows.map((r) => r.id))
    }

    const deselectAll = () => setSelectedStatements([])

    const toggleChecked = (id: string) => {
        setSelectedStatements((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]))
    }

    const dspOptions = useMemo(() => {
        const options = statementsStore.statements
            .map((row) => row.dsp)
            .filter((dsp, idx, arr) => arr.indexOf(dsp) === idx)
            .map((dsp, idx) => ({ label: dsp, value: dsp, id: (idx + 2).toString() }))
            .sort((a, b) => a.label.localeCompare(b.label))
        options.unshift({ label: 'All', value: 'All', id: '1' })
        return options
    }, [statementsStore.statements])

    useEffect(() => {
        const init = async () => {
            preloaderStore.openMainPreloader(true)

            try {
                await Promise.all([
                    statementsStore.latestRoyaltiesDate ? Promise.resolve() : statementsStore.loadLatestRoyaltiesDate(),
                ])

                statementsStore.setUsagePeriod(statementsStore.royaltiesUsagePeriodOptions[0])

                setSelectedDsp(dspOptions[0])

                const { year, month } = getUsagePeriod(statementsStore.royaltiesUsagePeriodOptions[0])
                await Promise.all([
                    statementsStore.royalties ? Promise.resolve() : statementsStore.loadRoyalties(year, month),
                    statementsStore.loadDistributionSummaryPdfLink(year, month),
                ])
            } finally {
                preloaderStore.openMainPreloader(false)
            }
        }

        void init()

        return () => {
            statementsStore.clearStatements()
            statementsStore.clearFilters()
            setSelectedPayee({ id: '', label: '', value: '' })
            setSelectedDsp({ id: '', label: '', value: '' })
        }
    }, [])

    return {
        selectedDsp,
        loading,
        selectAll,
        deselectAll,
        toggleChecked,
        selectedPayee,
        setPayeeAndLoadStatements,
        statementsStore,
        sidebarIsOpen,
        setSidebarIsOpen,
        filteredRows,
        selectedStatements,
        handleNavigateAndSetPeriod,
        useRoyaltiesForPeriod,
        dspOptions,
        setDsp,
        payeeOptionsForSelect,
    }
}
